import React, { useState, useEffect } from "react"
import {
    Box,
    Button,
    Chip,
    Container,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    Tabs,
    Tab,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { useTranslation } from "react-i18next"
import { useParams, useNavigate } from "react-router-dom"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import { useAuth } from "../../services/AuthContext"
import { getPrimaryRole, hasRoleAccess } from "../../services/navigationConfig"
import CustomerSalesTab from "../../components/customers/CustomerSalesTab"
import CustomerDocumentsTab from "../../components/customers/CustomerDocumentsTab"

const labelStyle = {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    color: "#6b7280",
    letterSpacing: "0.05em",
    mb: 0.5,
}

function DetailField({
    label,
    value,
    emptyLabel,
}: {
    label: React.ReactNode
    value: any
    emptyLabel: React.ReactNode
}) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={labelStyle}>{label}</Typography>
            <Typography variant="body1">{value || emptyLabel}</Typography>
        </Box>
    )
}

const CustomerDetailPage: React.FC = () => {
    const { t, i18n } = useTranslation()
    const { customerId } = useParams<{ customerId: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const primaryRole = getPrimaryRole(user?.roles)
    const isManager = hasRoleAccess(primaryRole, "manager")
    const [companyId, setCompanyId] = useState("")
    const [customer, setCustomer] = useState<any>(null)
    const [recentActivity, setRecentActivity] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [activeTab, setActiveTab] = useState(0)
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({
        open: false,
        message: "",
        severity: "success",
    })

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const list = res.companies || []
                if (list.length > 0) setCompanyId(list[0].id)
            } catch (e: any) {
                console.error("Failed to load companies:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!companyId || !customerId) return
        let cancelled = false
        ;(async () => {
            setLoading(true)
            try {
                const c = await adminService.getCustomer(companyId, customerId)
                if (cancelled) return
                setCustomer(c)
            } catch (e: any) {
                console.error("Failed to load customer:", e)
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId, customerId])

    useEffect(() => {
        if (!companyId || !customerId) return
        let cancelled = false
        ;(async () => {
            try {
                const res = await adminService.getCustomerRecentActivity(
                    companyId,
                    customerId
                )
                if (cancelled) return
                setRecentActivity(res.items || [])
            } catch (e: any) {
                console.error("Failed to load recent activity:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId, customerId])

    const handleDelete = async () => {
        if (!companyId || !customerId) return
        try {
            await adminService.deleteCustomer(companyId, customerId)
            navigate("/admin/customers")
        } catch (error: any) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                String(t("customersPages.detail.errors.deleteFailed"))
            setSnackbar({ open: true, message: msg, severity: "error" })
        }
        setDeleteDialogOpen(false)
    }

    const formatDate = (dateStr: string) => {
        if (!dateStr) return t("common.dash")
        return new Date(dateStr).toLocaleDateString(i18n.language || "fr", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        })
    }

    const formatAmount = (minorUnits: number | null | undefined) => {
        if (minorUnits == null) return t("common.dash")
        return new Intl.NumberFormat(i18n.language || "fr", {
            style: "currency",
            currency: "EUR",
        }).format(minorUnits / 100)
    }

    if (loading) {
        return (
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Container maxWidth="lg">
                    <Typography>
                        {t("customersPages.common.loading")}
                    </Typography>
                </Container>
            </Box>
        )
    }

    if (!customer) {
        return (
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Container maxWidth="lg">
                    <Typography>
                        {t("customersPages.common.notFound")}
                    </Typography>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin/customers")}
                        sx={{ mt: 2 }}
                    >
                        {t("customersPages.common.backToCustomers")}
                    </Button>
                </Container>
            </Box>
        )
    }

    const isBusiness = customer.type === "business"

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate("/admin/customers")}
                        >
                            {t("customersPages.common.back")}
                        </Button>
                        <Typography variant="h4">
                            {isBusiness ? customer.company_name : customer.name}
                        </Typography>
                        <Chip
                            label={t(`customerForm.types.${customer.type}`)}
                            size="small"
                            color={isBusiness ? "primary" : "default"}
                            variant="outlined"
                        />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() =>
                                navigate(`/admin/customers/${customerId}/edit`)
                            }
                        >
                            {t("customersPages.detail.actions.edit")}
                        </Button>
                        {isManager && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                {t("customersPages.detail.actions.delete")}
                            </Button>
                        )}
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Identity & Contact */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t(
                                    "customersPages.detail.sections.identityContact"
                                )}
                            </Typography>
                            {isBusiness && (
                                <DetailField
                                    label={t(
                                        "customersPages.detail.fields.tradeName"
                                    )}
                                    value={customer.company_name}
                                    emptyLabel={t("common.dash")}
                                />
                            )}
                            <DetailField
                                label={t("customersPages.detail.fields.name")}
                                value={customer.name}
                                emptyLabel={t("common.dash")}
                            />
                            <DetailField
                                label={t("customersPages.detail.fields.email")}
                                value={customer.email}
                                emptyLabel={t("common.dash")}
                            />
                            <DetailField
                                label={t("customersPages.detail.fields.phone")}
                                value={customer.phone}
                                emptyLabel={t("common.dash")}
                            />
                            <DetailField
                                label={t(
                                    "customersPages.detail.fields.customerType"
                                )}
                                value={customer.customer_type_name}
                                emptyLabel={t("common.dash")}
                            />
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={labelStyle}>
                                    {t(
                                        "customersPages.detail.fields.pricingTier"
                                    )}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="body1">
                                        {customer.pricing_tier_name ||
                                            t(
                                                "customersPages.detail.fields.defaultPricingTier"
                                            )}
                                    </Typography>
                                    {customer.pricing_tier_id &&
                                        customer.pricing_tier_active ===
                                            false && (
                                            <Chip
                                                icon={<WarningAmberIcon />}
                                                label={t(
                                                    "customersPages.detail.status.inactive"
                                                )}
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                            />
                                        )}
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Address */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t("customersPages.detail.sections.address")}
                            </Typography>
                            <DetailField
                                label={t("customersPages.detail.fields.street")}
                                value={customer.address_street}
                                emptyLabel={t("common.dash")}
                            />
                            <DetailField
                                label={t(
                                    "customersPages.detail.fields.zipCode"
                                )}
                                value={customer.zip_code}
                                emptyLabel={t("common.dash")}
                            />
                            <DetailField
                                label={t("customersPages.detail.fields.city")}
                                value={customer.city}
                                emptyLabel={t("common.dash")}
                            />
                            <DetailField
                                label={t(
                                    "customersPages.detail.fields.country"
                                )}
                                value={customer.country}
                                emptyLabel={t("common.dash")}
                            />
                        </Paper>
                    </Grid>

                    {/* Business Fields (conditional) */}
                    {isBusiness && (
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t(
                                        "customersPages.detail.sections.businessInfo"
                                    )}
                                </Typography>
                                <DetailField
                                    label={t(
                                        "customersPages.detail.fields.siret"
                                    )}
                                    value={customer.siret}
                                    emptyLabel={t("common.dash")}
                                />
                                <DetailField
                                    label={t(
                                        "customersPages.detail.fields.vatNumber"
                                    )}
                                    value={
                                        customer.vat_number || customer.vat_id
                                    }
                                    emptyLabel={t("common.dash")}
                                />
                            </Paper>
                        </Grid>
                    )}

                    {/* Recent Activity Summary */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t(
                                    "customersPages.detail.sections.recentActivity"
                                )}
                            </Typography>
                            {recentActivity.length === 0 ? (
                                <Typography color="text.secondary">
                                    {t(
                                        "customersPages.detail.empty.recentActivity"
                                    )}
                                </Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    {t(
                                                        "customersPages.detail.activity.columns.reference"
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {t(
                                                        "customersPages.detail.activity.columns.source"
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {t(
                                                        "customersPages.detail.activity.columns.type"
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {t(
                                                        "customersPages.detail.activity.columns.date"
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {t(
                                                        "customersPages.detail.activity.columns.total"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recentActivity.map((item) => (
                                                <TableRow
                                                    key={`${item.source}-${item.id}`}
                                                    hover
                                                >
                                                    <TableCell>
                                                        {item.reference ||
                                                            item.id}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={t(
                                                                `customersPages.detail.activity.source.${item.source}`
                                                            )}
                                                            size="small"
                                                            color={
                                                                item.source ===
                                                                "sale"
                                                                    ? "success"
                                                                    : "info"
                                                            }
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.type ||
                                                            t("common.dash")}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDate(
                                                            item.created_at
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatAmount(
                                                            item.total_minor
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Grid>

                    {/* Sales & Documents Tabs */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Tabs
                                value={activeTab}
                                onChange={(_, newValue) =>
                                    setActiveTab(newValue)
                                }
                                sx={{ mb: 2 }}
                            >
                                <Tab
                                    label={t(
                                        "customersPages.detail.tabs.sales"
                                    )}
                                />
                                <Tab
                                    label={t(
                                        "customersPages.detail.tabs.documents"
                                    )}
                                />
                            </Tabs>
                            {activeTab === 0 && companyId && customerId && (
                                <CustomerSalesTab
                                    companyId={companyId}
                                    customerId={customerId}
                                />
                            )}
                            {activeTab === 1 && companyId && customerId && (
                                <CustomerDocumentsTab
                                    companyId={companyId}
                                    customerId={customerId}
                                />
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>
                        {t("customersPages.detail.deleteDialog.title")}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t("customersPages.detail.deleteDialog.message")}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>
                            {t("common.cancel")}
                        </Button>
                        <Button
                            onClick={handleDelete}
                            color="error"
                            variant="contained"
                        >
                            {t("customersPages.detail.actions.delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    )
}

export default CustomerDetailPage
